// Bible Appendix B.2 — SMS templates. Only the first five are billed to the shop's SMS Points; the rest are NTechEdge-paid system SMS.
import { formatInt, formatTaka } from '../money';
import { PERSONAL_PRICE } from '../pricing';
import { smsCost } from './cost';
import type { MoneyInput } from '../money';
import type { SmsCost } from './cost';

export const SHOP_NAME_MAX = 16;
export const truncName = (name: string): string => {
  const chars = Array.from(name.trim());
  return chars.length <= SHOP_NAME_MAX ? chars.join('') : chars.slice(0, SHOP_NAME_MAX).join('');
};

export type SmsKind =
  | 'invoice'
  | 'due_reminder'
  | 'installment_reminder'
  | 'debt_reminder'
  | 'otp'
  | 'staff_otp'
  | 'trial_ending'
  | 'renewal_reminder'
  | 'payment_due'
  | 'new_device'
  | 'ownership_transfer'
  | 'admin_grant';

/** Charged to the shop wallet (Bible B.2: first five). Everything else is a system SMS (isSystem = true). */
export const SHOP_BILLED_KINDS: readonly SmsKind[] = ['invoice', 'due_reminder', 'installment_reminder', 'debt_reminder'];
export const isShopBilled = (k: SmsKind): boolean => SHOP_BILLED_KINDS.includes(k);

export interface BuiltSms extends SmsCost {
  body: string;
  lang: 'bn' | 'en';
}

const tk = (v: MoneyInput) => formatTaka(v, { lang: 'bn', fixed: false });
const tkEn = (v: MoneyInput) => formatTaka(v, { lang: 'en', symbol: false }).replace(/,/g, '');

const finish = (body: string, lang: 'bn' | 'en'): BuiltSms => ({ body, lang, ...smsCost(body) });

/**
 * Invoice SMS: Bangla if it fits one segment, else the English alternative (Guide §13.1 step 2).
 * Due part is dropped when nothing is due.
 */
export function invoiceSms(p: { shop: string; total: MoneyInput; due: MoneyInput; link: string }): BuiltSms {
  const shop = truncName(p.shop);
  const hasDue = Number(p.due) > 0;
  const bn = `${shop}: ${tk(p.total)} কেনাকাটা${hasDue ? `, বাকি ${tk(p.due)}` : ''}। রসিদ: ${p.link}`;
  const bnBuilt = finish(bn, 'bn');
  if (bnBuilt.segments <= 1) return bnBuilt;
  const en = `${shop}: Tk${tkEn(p.total)} purchase${hasDue ? `, due Tk${tkEn(p.due)}` : ''}. Receipt: ${p.link}`;
  const enBuilt = finish(en, 'en');
  // a Bangla shop name makes the English text UCS-2 too; never pay more than the cheaper of the two
  return enBuilt.segments <= bnBuilt.segments ? enBuilt : bnBuilt;
}

export const dueReminderSms = (p: { shop: string; due: MoneyInput; shopPhone: string }): BuiltSms =>
  finish(`${truncName(p.shop)}: আপনার বাকি ${tk(p.due)}। দয়া করে পরিশোধ করুন। ${p.shopPhone}`, 'bn');

export const installmentReminderSms = (p: { shop: string; amount: MoneyInput; shopPhone: string }): BuiltSms =>
  finish(`${truncName(p.shop)}: আজ কিস্তি ${tk(p.amount)} দেওয়ার দিন। ${p.shopPhone}`, 'bn');

export const debtReminderSms = (p: { name: string; amount: MoneyInput; date: string }): BuiltSms =>
  finish(`${p.name} মনে করিয়ে দিচ্ছেন: ধার ${tk(p.amount)}, ফেরতের তারিখ ${p.date}।`, 'bn');

export const otpSms = (code: string, appHost = 'app.dokaandigit.com'): BuiltSms =>
  finish(`Your DokaanDigit code is ${code}. Do not share it. Valid 5 min.\n@${appHost} #${code}`, 'en');

export const staffOtpSms = (p: { shop: string; code: string }): BuiltSms =>
  finish(`${truncName(p.shop)}-এ Staff হিসেবে যোগ হওয়ার কোড ${p.code}। কাউকে বলবেন না।`, 'bn');

export const trialEndingSms = (p: { days: number; link: string }): BuiltSms =>
  finish(`DokaanDigit: ট্রায়াল ${formatInt(p.days)} দিনে শেষ। তারপরও বিক্রি ও বাকি ফ্রি চলবে। প্ল্যান দেখুন: ${p.link}`, 'bn');

export const renewalReminderSms = (p: { planLabel: string; date: string; link: string }): BuiltSms =>
  finish(`DokaanDigit: ${p.planLabel} প্ল্যানের মেয়াদ ${p.date}-এ শেষ। রিনিউ: ${p.link}`, 'bn');

export const paymentDueSms = (p: { days: number; link: string }): BuiltSms =>
  finish(`DokaanDigit: পেমেন্ট বাকি। ${formatInt(p.days)} দিনে রিনিউ না করলে Free প্ল্যানে যাবে, ডেটা থাকবে। ${p.link}`, 'bn');

export const personalTrialEndingSms = (p: { days: number; link: string }): BuiltSms =>
  finish(`DokaanDigit: Personal Savings ট্রায়াল ${formatInt(p.days)} দিনে শেষ। এরপর নতুন এন্ট্রি বন্ধ থাকবে, ডেটা ও Export থাকবে। ৳${formatInt(PERSONAL_PRICE.monthly)}/মাস: ${p.link}`, 'bn');

export const personalRenewalSms = (p: { date: string; link: string }): BuiltSms =>
  finish(`DokaanDigit: Personal Savings-এর মেয়াদ ${p.date}-এ শেষ। রিনিউ: ${p.link}`, 'bn');

export const personalRestrictedSms = (p: { link: string }): BuiltSms =>
  finish(`DokaanDigit: Personal Savings-এর মেয়াদ শেষ। আপনার সব ডেটা নিরাপদ, দেখা ও Export চলবে; নতুন এন্ট্রির জন্য পেমেন্ট করুন: ${p.link}`, 'bn');

export const newDeviceSms = (supportPhone: string): BuiltSms =>
  finish(`DokaanDigit: নতুন ডিভাইসে আপনার Account-এ লগইন হয়েছে। আপনি না হলে এখনই ${supportPhone}-এ কল করুন।`, 'bn');

export const ownershipTransferSms = (link: string): BuiltSms =>
  finish(`DokaanDigit: আপনার Account-এর মালিকানা বদলের অনুরোধ এসেছে। আপনি না হলে ৪৮ ঘণ্টার মধ্যে বাতিল করুন: ${link}`, 'bn');

export const formatDurationPhraseBn = (days: number): string => {
  if (days >= 365 && days % 365 === 0) {
    const years = Math.round(days / 365);
    return `${formatInt(years)} বছরের জন্য`;
  }
  if (days >= 360 && days <= 370) {
    return '১ বছরের জন্য';
  }
  if (days >= 720 && days <= 740) {
    return '২ বছরের জন্য';
  }
  if (days >= 28 && days <= 31) {
    return '১ মাসের জন্য';
  }
  if (days >= 58 && days <= 62) {
    return '২ মাসের জন্য';
  }
  if (days >= 88 && days <= 93) {
    return '৩ মাসের জন্য';
  }
  if (days >= 178 && days <= 184) {
    return '৬ মাসের জন্য';
  }
  if (days >= 30 && days % 30 === 0) {
    const months = Math.round(days / 30);
    return `${formatInt(months)} মাসের জন্য`;
  }
  return `${formatInt(days)} দিনের জন্য`;
};

// NTechEdge gave the account something — the owner is told in the app AND by SMS (system SMS, not charged to the shop)
export const planGrantSms = (p: { planLabel: string; days: number; date: string }): BuiltSms => {
  const dur = formatDurationPhraseBn(p.days);
  return finish(`DokaanDigit: আপনার ${p.planLabel} প্যাকেজ ${dur} সফলভাবে চালু করা হয়েছে (${p.date} পর্যন্ত)। অ্যাপ খুলে সব ফিচার ব্যবহার করুন।`, 'bn');
};

export const trialExtendedSms = (p: { days: number; date: string }): BuiltSms =>
  finish(`DokaanDigit: আপনার ফ্রি ট্রায়াল ${formatInt(p.days)} দিন বাড়ানো হয়েছে, এখন ${p.date} পর্যন্ত চলবে।`, 'bn');

export const smsPointsGrantedSms = (points: number): BuiltSms =>
  finish(`DokaanDigit: আপনার SMS Wallet-এ ${formatInt(points)} Point যোগ হয়েছে। অ্যাপে "SMS Point" থেকে দেখুন।`, 'bn');

export const adminMessageSms = (text: string): BuiltSms => finish(`DokaanDigit: ${text.trim()}`, 'bn');
